import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';

const navLinks = [
  { name: 'Services', href: '/#services', isHash: true },
  { name: 'Windscreen Calibration', href: '/windscreen-calibration-adelaide', isHash: false },
  { name: 'Why Us', href: '/#why-us', isHash: true },
  { name: 'Process', href: '/#process', isHash: true },
  { name: 'Industries', href: '/#industries', isHash: true },
  { name: 'FAQ', href: '/#faq', isHash: true },
  { name: 'Contact', href: '/#contact', isHash: true },
];

const services = [
  'Static ADAS Calibration',
  'Dynamic ADAS Calibration',
  'Pre & Post-Repair Scanning',
  'Windscreen Camera Calibration',
  'OEM Documentation',
  'Mobile Services',
];

export const Footer = () => {
  const location = useLocation();

  const handleNavClick = (e, link) => {
    if (link.isHash && location.pathname === '/') {
      e.preventDefault();
      const sectionId = link.href.replace('/#', '#');
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-slate-950 py-16" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand Column */}
          <div>
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_b16acfe5-1235-44c1-ace5-482b8c36602b/artifacts/14ftomu2_Advanced-ADAS-w-logo-white-with-red-768x158.png"
                alt="Advanced ADAS Calibrations"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Independent ADAS calibration specialists serving South Australia&apos;s 
              automotive industry with OEM-compliant procedures and insurer-ready 
              documentation.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-white mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.name}>
                  {link.isHash ? (
                    <a
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link)}
                      className="text-slate-400 hover:text-white text-sm transition-colors"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className={`text-sm transition-colors ${
                        location.pathname === link.href 
                          ? 'text-blue-400' 
                          : 'text-slate-400 hover:text-white'
                      }`}
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-bold text-white mb-4">
              Services
            </h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-slate-400 text-sm">
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-bold text-white mb-4">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:0402454588" 
                  className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">0402 454 588</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:sales@advancedadas.com.au" 
                  className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">sales@advancedadas.com.au</span>
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3 text-slate-400">
                  <MapPin className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">South Australia</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm text-center md:text-left">
              © {new Date().getFullYear()} Advanced ADAS Calibrations. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
